import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "@/App";
import { toast } from "sonner";
import { Search, Tent, MapPin, Calendar, Users, Bell, Zap, Eye, MessageCircle, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Recently Viewed Helper Functions
const RECENTLY_VIEWED_KEY = 'campsight_recently_viewed';
const MAX_RECENTLY_VIEWED = 6;

const getRecentlyViewed = () => {
  try {
    const stored = localStorage.getItem(RECENTLY_VIEWED_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const addToRecentlyViewed = (campsite) => {
  try {
    const current = getRecentlyViewed();
    // Remove if already exists
    const filtered = current.filter(c => c.id !== campsite.id);
    // Add to front
    const updated = [campsite, ...filtered].slice(0, MAX_RECENTLY_VIEWED);
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(updated));
    return updated;
  } catch {
    return [];
  }
};

const LandingPage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [campsites, setCampsites] = useState([]);
  const [parks, setParks] = useState([]);
  const [selectedPark, setSelectedPark] = useState("");
  const [siteType, setSiteType] = useState("");
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [hasElectrical, setHasElectrical] = useState(false);
  const [hasWaterfront, setHasWaterfront] = useState(false);
  const [hasShowers, setHasShowers] = useState(false);
  const [hasBackcountry, setHasBackcountry] = useState(false);
  const [showAlertForm, setShowAlertForm] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10);
  const [alertSubmitted, setAlertSubmitted] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [alertData, setAlertData] = useState({
    park: "",
    site_type: "",
    site_number: "",
    start_date: "",
    end_date: "",
    email: ""
  });

  useEffect(() => {
    // Load recently viewed from localStorage
    setRecentlyViewed(getRecentlyViewed());
    
    // Check for session_id in URL fragment
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      const sessionId = hash.split("session_id=")[1].split("&")[0];
      processSession(sessionId);
      // Clean URL
      window.history.replaceState(null, "", window.location.pathname);
    } else {
      fetchParks();
      fetchCampsites();
    }
  }, []);

  const fetchParks = async () => {
    try {
      const response = await axiosInstance.get("/parks");
      setParks(response.data.parks);
    } catch (error) {
      console.error("Error fetching parks:", error);
    }
  };

  const processSession = async (sessionId) => {
    setLoading(true);
    try {
      const response = await axiosInstance.post("/auth/session", { session_id: sessionId });
      setUser(response.data.user);
      toast.success("Welcome to CampSight!");
      // Wait a moment for cookie to be set, then navigate
      setTimeout(() => {
        navigate("/dashboard");
      }, 500);
    } catch (error) {
      toast.error("Authentication failed: " + (error.response?.data?.detail || error.message));
      console.error("Auth error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCampsites = async (shouldScroll = false) => {
    setLoading(true);
    try {
      const params = {};
      if (selectedPark && selectedPark !== "all") params.location = selectedPark;
      if (siteType && siteType !== "all") params.site_type = siteType;
      if (hasElectrical) params.has_electrical = true;
      if (hasWaterfront) params.has_waterfront = true;
      if (hasShowers) params.has_showers = true;
      if (hasBackcountry) params.has_backcountry = true;
      
      // Quick Win #1: Add date filtering
      if (checkInDate) {
        params.check_in = checkInDate.toISOString().split('T')[0];
      }
      if (checkOutDate) {
        params.check_out = checkOutDate.toISOString().split('T')[0];
      }
      
      const response = await axiosInstance.get("/campsites", { params });
      
      // Sort campsites: Open parks first, then closed parks
      const sortedCampsites = response.data.sort((a, b) => {
        // available parks (true) come before unavailable (false)
        if (a.available === b.available) return 0;
        return a.available ? -1 : 1;
      });
      
      setCampsites(sortedCampsites);
      
      // Only scroll to results if user clicked search button
      if (shouldScroll) {
        setTimeout(() => {
          const resultsSection = document.getElementById('featured-campsites');
          if (resultsSection) {
            resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
      
      // Show appropriate message based on date selection
      if (checkInDate && checkOutDate) {
        toast.success(`Found ${sortedCampsites.length} available campsites for your dates!`);
      } else {
        toast.success(`Found ${sortedCampsites.length} campsites matching your criteria!`);
      }
    } catch (error) {
      console.error("Error fetching campsites:", error);
      toast.error("Failed to load campsites. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle viewing a campsite - tracks in recently viewed
  const handleViewCampsite = (campsite) => {
    // Save minimal data needed for recently viewed display
    const campsiteData = {
      id: campsite.id,
      park_name: campsite.park_name,
      name: campsite.name,
      location: campsite.location,
      images: campsite.images,
      price_per_night: campsite.price_per_night,
      available: campsite.available,
      availability_level: campsite.availability_level
    };
    const updated = addToRecentlyViewed(campsiteData);
    setRecentlyViewed(updated);
    navigate(`/campsite/${campsite.id}`);
  };

  const handleLogin = () => {
    navigate("/login");
  };

  const handleCreateAlertClick = () => {
    if (!user) {
      toast.info("Please sign in to create availability alerts");
      navigate("/login");
      return;
    }
    
    setShowAlertForm(true);
  };

  const handleSearch = () => {
    setVisibleCount(10); // Reset to show 10 campsites when searching
    fetchCampsites(true); // Pass true to enable auto-scroll
  };

  const handleShowMore = () => {
    setVisibleCount(prevCount => prevCount + 10); // Add 10 more campsites
  };

  const handleCreateAlert = async (e) => {
    e.preventDefault();
    
    if (!alertData.park || !alertData.site_type || !alertData.site_number || 
        !alertData.start_date || !alertData.end_date || !alertData.email) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      await axiosInstance.post("/alerts/create", alertData);
      toast.success("Alert created! Check your email for confirmation.");
      setAlertSubmitted(true);
      setAlertData({
        park: "",
        site_type: "",
        site_number: "",
        start_date: "",
        end_date: "",
        email: ""
      });
      // Close dialog after 2 seconds to show success message
      setTimeout(() => {
        setShowAlertForm(false);
        setAlertSubmitted(false);
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create alert");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 to-beige-100">
        <div className="text-xl font-medium">Authenticating...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-stone-50 to-orange-50">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-10 bg-transparent">
        <div className="max-w-7xl mx-auto px-6 py-6 flex justify-end items-center">
          {!user && (
            <Button 
              data-testid="login-button"
              onClick={handleLogin} 
              className="px-6 py-2 h-auto text-white font-semibold hover:opacity-90"
              style={{background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)'}}
            >
              Sign In
            </Button>
          )}
          {user && (
            <Button 
              data-testid="dashboard-button"
              onClick={() => navigate("/dashboard")} 
              className="px-6 py-2 h-auto text-white font-semibold hover:opacity-90"
              style={{background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)'}}
            >
              Dashboard
            </Button>
          )}
        </div>
      </header>

      {/* Hero Section with Background Image */}
      <section 
        className="relative min-h-[500px] sm:min-h-[600px] flex items-center justify-center px-4 sm:px-6 py-16 sm:py-20"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(https://customer-assets.emergentagent.com/job_tentping/artifacts/hicvfqdl_PXL_20231028_172234292.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'scroll'
        }}
      >
        <div className="max-w-4xl mx-auto w-full text-center pt-12 sm:pt-0">
          <div className="flex items-center justify-center gap-4 mb-2 sm:mb-3">
            <img src="https://customer-assets.emergentagent.com/job_sitehunter-1/artifacts/rlsp35z7_CampPing%20UI%20Icon.png" alt="CampSight" className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24" />
            <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl" style={{fontFamily: 'Lobster Two, cursive', fontWeight: 700, letterSpacing: '0.02em', color: '#FFF4E6', textShadow: '2px 2px 4px rgba(0, 0, 0, 0.4)'}}>
              CampSight
            </h1>
          </div>
          <p className="text-2xl sm:text-3xl md:text-4xl mb-6 sm:mb-10 drop-shadow-lg" style={{fontFamily: 'Lobster Two, cursive', fontWeight: 700, color: '#FFF4E6', textShadow: '2px 2px 4px rgba(0, 0, 0, 0.4)'}}>
            Camp at first Sight.
          </p>

          {/* Search Bar Overlay - Mobile Optimized */}
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-8 shadow-2xl max-w-3xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-3 sm:mb-4">
              {/* Check-in Date */}
              <div className="relative">
                <DatePicker
                  selected={checkInDate}
                  onChange={(date) => setCheckInDate(date)}
                  selectsStart
                  startDate={checkInDate}
                  endDate={checkOutDate}
                  minDate={new Date()}
                  placeholderText="Check-in"
                  className="w-full h-11 sm:h-14 px-3 sm:px-4 text-sm sm:text-base border-2 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  dateFormat="MMM d, yyyy"
                />
                <Calendar className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
              </div>

              {/* Check-out Date */}
              <div className="relative">
                <DatePicker
                  selected={checkOutDate}
                  onChange={(date) => setCheckOutDate(date)}
                  selectsEnd
                  startDate={checkInDate}
                  endDate={checkOutDate}
                  minDate={checkInDate || new Date()}
                  placeholderText="Check-out"
                  className="w-full h-11 sm:h-14 px-3 sm:px-4 text-sm sm:text-base border-2 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  dateFormat="MMM d, yyyy"
                />
                <Calendar className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
              </div>

              <Select value={selectedPark} onValueChange={setSelectedPark}>
                <SelectTrigger data-testid="park-select" className="h-11 sm:h-14 text-sm sm:text-base border-2">
                  <SelectValue placeholder="Park" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Parks</SelectItem>
                  {parks.map((park) => (
                    <SelectItem key={park} value={park}>
                      {park}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={siteType} onValueChange={setSiteType}>
                <SelectTrigger data-testid="site-type-select" className="h-11 sm:h-14 text-sm sm:text-base border-2">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="tent">Tent</SelectItem>
                  <SelectItem value="rv">RV</SelectItem>
                  <SelectItem value="cabin">Cabin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Amenity Filters - Mobile Optimized */}
            <div className="flex flex-wrap gap-2 sm:gap-4 mb-3 sm:mb-4 justify-center">
              <label className="flex items-center gap-1.5 sm:gap-2 cursor-pointer bg-white/50 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full hover:bg-white/70 transition-colors">
                <input
                  type="checkbox"
                  checked={hasElectrical}
                  onChange={(e) => setHasElectrical(e.target.checked)}
                  className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                />
                <span className="text-xs sm:text-sm font-medium text-gray-700">⚡ <span className="hidden sm:inline">Electrical</span></span>
              </label>
              <label className="flex items-center gap-1.5 sm:gap-2 cursor-pointer bg-white/50 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full hover:bg-white/70 transition-colors">
                <input
                  type="checkbox"
                  checked={hasWaterfront}
                  onChange={(e) => setHasWaterfront(e.target.checked)}
                  className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                />
                <span className="text-xs sm:text-sm font-medium text-gray-700">🌊 <span className="hidden sm:inline">Waterfront</span></span>
              </label>
              <label className="flex items-center gap-1.5 sm:gap-2 cursor-pointer bg-white/50 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full hover:bg-white/70 transition-colors">
                <input
                  type="checkbox"
                  checked={hasShowers}
                  onChange={(e) => setHasShowers(e.target.checked)}
                  className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded border-gray-300 text-green-500 focus:ring-green-500"
                />
                <span className="text-xs sm:text-sm font-medium text-gray-700">🚿 <span className="hidden sm:inline">Showers</span></span>
              </label>
              <label className="flex items-center gap-1.5 sm:gap-2 cursor-pointer bg-white/50 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full hover:bg-white/70 transition-colors">
                <input
                  type="checkbox"
                  checked={hasBackcountry}
                  onChange={(e) => setHasBackcountry(e.target.checked)}
                  className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-600"
                />
                <span className="text-xs sm:text-sm font-medium text-gray-700">🏕️ <span className="hidden sm:inline">Backcountry</span></span>
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              <Button 
                data-testid="search-button"
                onClick={handleSearch} 
                className="h-11 sm:h-14 text-base sm:text-lg font-semibold"
                style={{background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)'}}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    Explore Campsites
                  </>
                )}
              </Button>
              <Button 
                data-testid="create-alert-button"
                onClick={handleCreateAlertClick} 
                className="h-11 sm:h-14 text-base sm:text-lg font-semibold"
                style={{background: 'linear-gradient(135deg, #2D5016 0%, #0D7377 100%)', color: 'white'}}
              >
                <Bell className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                <span className="hidden sm:inline">Create Availability Alert</span>
                <span className="sm:hidden">Set Alert</span>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="bg-gray-50 py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-2 sm:mb-4" style={{color: '#2D5016', fontFamily: 'Lobster Two, cursive', fontWeight: 700}}>
              How It Works
            </h2>
            <p className="text-lg sm:text-2xl text-gray-600">
              Never miss a campsite booking again
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {/* Real-Time Search */}
            <div className="text-center">
              <div className="mb-3 sm:mb-6 flex justify-center">
                <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full flex items-center justify-center" style={{backgroundColor: '#FFE8E0'}}>
                  <Search className="w-8 h-8 sm:w-12 sm:h-12" style={{color: '#FF6B35'}} />
                </div>
              </div>
              <h3 className="text-lg sm:text-2xl font-bold mb-2 sm:mb-4" style={{color: '#2D5016', fontFamily: 'Lobster Two, cursive', fontWeight: 700}}>
                Real-Time Search
              </h3>
              <p className="text-gray-600 text-sm sm:text-lg leading-relaxed">
                Browse thousands of campsites with up-to-date availability
              </p>
            </div>

            {/* Instant Alerts */}
            <div className="text-center">
              <div className="mb-3 sm:mb-6 flex justify-center">
                <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full flex items-center justify-center" style={{backgroundColor: '#FFE8E0'}}>
                  <Bell className="w-8 h-8 sm:w-12 sm:h-12" style={{color: '#FF6B35'}} />
                </div>
              </div>
              <h3 className="text-lg sm:text-2xl font-bold mb-2 sm:mb-4" style={{color: '#2D5016', fontFamily: 'Lobster Two, cursive', fontWeight: 700}}>
                Instant Alerts
              </h3>
              <p className="text-gray-600 text-sm sm:text-lg leading-relaxed">
                Get notified when your desired campsite becomes available
              </p>
            </div>

            {/* Detailed Info */}
            <div className="text-center">
              <div className="mb-3 sm:mb-6 flex justify-center">
                <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full flex items-center justify-center" style={{backgroundColor: '#FFE8E0'}}>
                  <Eye className="w-8 h-8 sm:w-12 sm:h-12" style={{color: '#FF6B35'}} />
                </div>
              </div>
              <h3 className="text-lg sm:text-2xl font-bold mb-2 sm:mb-4" style={{color: '#2D5016', fontFamily: 'Lobster Two, cursive', fontWeight: 700}}>
                Detailed Info
              </h3>
              <p className="text-gray-600 text-sm sm:text-lg leading-relaxed">
                View amenities, photos, and reviews for every park
              </p>
            </div>

            {/* Lightning Fast */}
            <div className="text-center">
              <div className="mb-3 sm:mb-6 flex justify-center">
                <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full flex items-center justify-center" style={{backgroundColor: '#FFE8E0'}}>
                  <Zap className="w-8 h-8 sm:w-12 sm:h-12" style={{color: '#FF6B35'}} />
                </div>
              </div>
              <h3 className="text-lg sm:text-2xl font-bold mb-2 sm:mb-4" style={{color: '#2D5016', fontFamily: 'Lobster Two, cursive', fontWeight: 700}}>
                Lightning Fast
              </h3>
              <p className="text-gray-600 text-sm sm:text-lg leading-relaxed">
                Book before others with instant notifications
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Most Popular Parks Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-4xl font-bold mb-2 sm:mb-4" style={{color: '#2D5016', fontFamily: 'Lobster Two, cursive', fontWeight: 700}}>
            Most Popular Parks
          </h2>
          <p className="text-base sm:text-xl text-gray-600">
            Discover Ontario&apos;s most sought-after camping destinations
          </p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {["Algonquin", "Killarney", "Pinery", "Sleeping Giant", "Sandbanks", "Silent Lake", "MacGregor Point", "Bon Echo", "Presqu'ile", "Arrowhead"].map((park) => {
            
            return (
              <div 
                key={park}
                onClick={async () => {
                  try {
                    // Fetch all campsites for this park
                    const response = await axiosInstance.get("/campsites", { 
                      params: { location: park } 
                    });
                    
                    if (response.data && response.data.length > 0) {
                      // Navigate to the first campsite from this park
                      navigate(`/campsite/${response.data[0].id}`);
                    } else {
                      toast.error(`No campsite found for ${park}`);
                    }
                  } catch (error) {
                    console.error("Error finding campsite:", error);
                    toast.error("Failed to load campsite details");
                  }
                }}
                className="bg-white rounded-lg p-4 shadow-md hover:shadow-xl transition-shadow cursor-pointer border-2 border-transparent hover:border-orange-500"
              >
                <div className="text-center">
                  <Tent className="w-8 h-8 mx-auto mb-2" style={{color: '#2D5016'}} />
                  <h4 className="font-semibold text-sm" style={{color: '#2D5016'}}>{park}</h4>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Recently Viewed Section */}
      {recentlyViewed.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 sm:w-6 sm:h-6" style={{color: '#2D5016'}} />
              <h2 className="text-xl sm:text-2xl font-bold" style={{color: '#2D5016', fontFamily: 'Lobster Two, cursive', fontWeight: 700}}>
                Recently Viewed
              </h2>
            </div>
            <button 
              onClick={() => {
                localStorage.removeItem(RECENTLY_VIEWED_KEY);
                setRecentlyViewed([]);
              }}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Clear
            </button>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {recentlyViewed.map((campsite) => (
              <div
                key={campsite.id}
                onClick={() => navigate(`/campsite/${campsite.id}`)}
                className="group cursor-pointer"
                data-testid={`recently-viewed-${campsite.id}`}
              >
                <div 
                  className="relative h-32 sm:h-40 rounded-xl overflow-hidden mb-2 transition-transform group-hover:scale-105"
                  style={{
                    backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.6) 100%), url(${campsite.images?.[0] || ''})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  {/* Availability indicator */}
                  <div className="absolute top-2 right-2">
                    <span className={`w-2.5 h-2.5 rounded-full inline-block ${
                      !campsite.available ? 'bg-red-500' :
                      campsite.availability_level === 'high' ? 'bg-green-500' :
                      campsite.availability_level === 'medium' ? 'bg-yellow-500' : 'bg-orange-500'
                    }`}></span>
                  </div>
                  {/* Price */}
                  <div className="absolute bottom-2 left-2">
                    <span className="text-white font-bold text-sm">${campsite.price_per_night}</span>
                    <span className="text-white/80 text-xs">/night</span>
                  </div>
                </div>
                <h4 className="font-semibold text-sm truncate" style={{color: '#2D5016'}}>{campsite.park_name}</h4>
                <p className="text-xs text-gray-500 truncate">{campsite.location}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Immersive Campsite Cards */}
      <section id="featured-campsites" className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <h3 className="text-3xl sm:text-4xl font-bold mb-8 sm:mb-12 text-center" style={{color: '#2D5016', fontFamily: 'Lobster Two, cursive', fontWeight: 700}}>
          Featured Campsites
        </h3>
        
        {/* Quick Win #4: Loading Skeletons */}
        {loading && campsites.length === 0 && (
          <div className="grid grid-cols-1 gap-6 sm:gap-8" data-testid="loading-skeletons">
            {[1, 2, 3].map((i) => (
              <div key={i} className="relative h-80 sm:h-[28rem] rounded-2xl sm:rounded-3xl overflow-hidden bg-gray-200 animate-pulse">
                <div className="absolute inset-0 flex flex-col justify-end p-4 sm:p-8">
                  <div className="h-8 bg-gray-300 rounded w-3/4 mb-3"></div>
                  <div className="h-4 bg-gray-300 rounded w-1/2 mb-3"></div>
                  <div className="h-4 bg-gray-300 rounded w-full mb-4"></div>
                  <div className="flex gap-2 mb-4">
                    <div className="h-6 w-20 bg-gray-300 rounded-full"></div>
                    <div className="h-6 w-20 bg-gray-300 rounded-full"></div>
                    <div className="h-6 w-20 bg-gray-300 rounded-full"></div>
                  </div>
                  <div className="h-20 bg-gray-300 rounded-xl"></div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Campsite Cards */}
        <div className="grid grid-cols-1 gap-6 sm:gap-8" data-testid="campsites-grid">
          {campsites.slice(0, visibleCount).map((campsite) => (
            <div 
              key={campsite.id} 
              className="relative h-80 sm:h-[28rem] rounded-2xl sm:rounded-3xl overflow-hidden group cursor-pointer transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
              data-testid={`campsite-card-${campsite.id}`}
              onClick={() => handleViewCampsite(campsite)}
              style={{
                backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.75) 100%), url(${campsite.images[0]})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              {/* Content Overlay */}
              <div className="absolute inset-0 flex flex-col justify-end p-4 sm:p-8 transition-all">
                {/* Quick Win #3: Enhanced Availability Badges */}
                <div className="absolute top-4 sm:top-6 right-4 sm:right-6 flex flex-col gap-2 items-end">
                  {!campsite.available ? (
                    <span className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-red-600/90 backdrop-blur-sm rounded-full text-xs sm:text-sm font-semibold text-white shadow-lg">
                      <span className="w-2 h-2 rounded-full bg-red-300 animate-pulse"></span>
                      Closed for Season
                    </span>
                  ) : (
                    <>
                      <span className={`inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 backdrop-blur-sm rounded-full text-xs sm:text-sm font-semibold text-white shadow-lg ${
                        campsite.availability_level === 'high' ? 'bg-green-600/90' :
                        campsite.availability_level === 'medium' ? 'bg-yellow-500/90' : 'bg-orange-500/90'
                      }`}>
                        <span className={`w-2 h-2 rounded-full animate-pulse ${
                          campsite.availability_level === 'high' ? 'bg-green-300' :
                          campsite.availability_level === 'medium' ? 'bg-yellow-200' : 'bg-orange-300'
                        }`}></span>
                        {campsite.availability_level === 'high' ? 'High Availability' :
                         campsite.availability_level === 'medium' ? 'Medium Availability' : 'Low Availability'}
                      </span>
                      <span className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600/90 backdrop-blur-sm rounded-full text-xs sm:text-sm font-semibold text-white shadow-lg">
                        {campsite.available_sites_count} sites open
                      </span>
                    </>
                  )}
                </div>
                
                <h4 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                  {campsite.park_name}
                </h4>
                <div className="flex items-center text-white/90 mb-2 sm:mb-3">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
                  <span className="text-base sm:text-lg truncate">{campsite.name} • {campsite.location}</span>
                </div>
                <p className="text-white/80 text-sm sm:text-lg mb-3 sm:mb-4 max-w-2xl line-clamp-2">
                  {campsite.description}
                </p>
                
                {/* Amenity Badges - Quick Win #5: Mobile responsive */}
                <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                  {campsite.has_electrical && (
                    <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-yellow-500/80 backdrop-blur-sm rounded-full text-xs sm:text-sm font-medium text-white flex items-center gap-1">
                      ⚡ <span className="hidden sm:inline">Electrical</span>
                    </span>
                  )}
                  {campsite.has_waterfront && (
                    <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-blue-500/80 backdrop-blur-sm rounded-full text-xs sm:text-sm font-medium text-white flex items-center gap-1">
                      🌊 <span className="hidden sm:inline">Waterfront</span>
                    </span>
                  )}
                  {campsite.has_showers && (
                    <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-green-500/80 backdrop-blur-sm rounded-full text-xs sm:text-sm font-medium text-white flex items-center gap-1">
                      🚿 <span className="hidden sm:inline">Showers</span>
                    </span>
                  )}
                  {campsite.has_backcountry && (
                    <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-emerald-600/80 backdrop-blur-sm rounded-full text-xs sm:text-sm font-medium text-white flex items-center gap-1">
                      🏕️ <span className="hidden sm:inline">Backcountry</span>
                    </span>
                  )}
                  {campsite.radio_free && (
                    <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-purple-500/80 backdrop-blur-sm rounded-full text-xs sm:text-sm font-medium text-white flex items-center gap-1">
                      📻 <span className="hidden sm:inline">Radio-Free</span>
                    </span>
                  )}
                </div>

                {/* Site Availability Bar */}
                <div className="bg-white/20 backdrop-blur-md rounded-xl p-3 sm:p-4 mb-3 sm:mb-4 border border-white/30">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-semibold text-sm sm:text-lg mb-1">
                        {campsite.available_sites_count > 0 ? (
                          <span className="flex items-center gap-2">
                            <span className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${
                              campsite.availability_level === 'high' ? 'bg-green-400' :
                              campsite.availability_level === 'medium' ? 'bg-yellow-400' : 'bg-red-400'
                            }`}></span>
                            <span className="truncate">{campsite.available_sites_count} of {campsite.total_sites} Sites Available</span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-gray-400"></span>
                            Closed for Season
                          </span>
                        )}
                      </div>
                      {campsite.available_site_numbers && campsite.available_site_numbers.length > 0 && (
                        <div className="text-white/90 text-xs sm:text-sm truncate">
                          Sites: {campsite.available_site_numbers.slice(0, 5).join(', ')}
                          {campsite.available_site_numbers.length > 5 && ` +${campsite.available_site_numbers.length - 5} more`}
                        </div>
                      )}
                    </div>
                    {campsite.available_sites_count > 0 && (
                      <div className="text-right flex-shrink-0">
                        <div className={`text-xl sm:text-2xl font-bold ${
                          campsite.availability_level === 'high' ? 'text-green-300' :
                          campsite.availability_level === 'medium' ? 'text-yellow-300' : 'text-red-300'
                        }`}>
                          {Math.round((campsite.available_sites_count / campsite.total_sites) * 100)}%
                        </div>
                        <div className="text-white/80 text-[10px] sm:text-xs uppercase tracking-wide">
                          available
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between flex-wrap gap-2 sm:gap-3">
                  <span className="text-2xl sm:text-3xl font-bold text-white">
                    ${campsite.price_per_night}
                    <span className="text-sm sm:text-lg font-normal">/night</span>
                  </span>
                  <div className="flex gap-2 sm:gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!user) {
                          toast.info("Please sign in to create availability alerts");
                          navigate("/login");
                          return;
                        }
                        setAlertData({...alertData, park: campsite.park_name});
                        setShowAlertForm(true);
                      }}
                      className="bg-orange-500/90 backdrop-blur-sm px-3 sm:px-5 py-2 sm:py-3 rounded-xl border-2 border-white/30 text-white font-semibold hover:bg-orange-600/90 transition-colors flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
                    >
                      <Bell className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Notify Me</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewCampsite(campsite);
                      }}
                      className="bg-white/20 backdrop-blur-sm px-3 sm:px-5 py-2 sm:py-3 rounded-xl border-2 border-white/30 text-white font-semibold hover:bg-white/30 transition-colors text-sm sm:text-base"
                    >
                      <span className="sm:hidden">Details</span>
                      <span className="hidden sm:inline">View Details →</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Show More Button */}
        {visibleCount < campsites.length && (
          <div className="flex justify-center mt-12">
            <Button 
              onClick={handleShowMore}
              className="h-14 px-8 text-lg font-semibold"
              style={{background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)'}}
            >
              Show more available
            </Button>
          </div>
        )}
      </section>

      {/* Create Alert Dialog */}
      <Dialog open={showAlertForm} onOpenChange={setShowAlertForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Site Availability Alert</DialogTitle>
          </DialogHeader>
          
          {alertSubmitted ? (
            <div className="py-8 text-center">
              <div className="mb-4">
                <svg className="mx-auto h-16 w-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2" style={{color: '#2D5016'}}>Alert Created Successfully!</h3>
              <p className="text-gray-600">Check your email for confirmation.</p>
            </div>
          ) : (
          <form onSubmit={handleCreateAlert} className="space-y-4">
            <div>
              <Label htmlFor="alert_park">Park</Label>
              <Select value={alertData.park} onValueChange={(value) => setAlertData({...alertData, park: value})}>
                <SelectTrigger id="alert_park">
                  <SelectValue placeholder="Select a Park" />
                </SelectTrigger>
                <SelectContent>
                  {parks.map((park) => (
                    <SelectItem key={park} value={park}>
                      {park}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="alert_site_type">Site Type</Label>
              <Select value={alertData.site_type} onValueChange={(value) => setAlertData({...alertData, site_type: value})}>
                <SelectTrigger id="alert_site_type">
                  <SelectValue placeholder="Site Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tent">Tent</SelectItem>
                  <SelectItem value="rv">RV</SelectItem>
                  <SelectItem value="cabin">Cabin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="alert_site_number">Site Number</Label>
              <Input
                id="alert_site_number"
                type="number"
                placeholder="e.g., 45"
                value={alertData.site_number}
                onChange={(e) => setAlertData({...alertData, site_number: e.target.value})}
                required
                min="1"
              />
            </div>
            <div>
              <Label htmlFor="alert_start_date">Start Date</Label>
              <Input
                id="alert_start_date"
                type="date"
                value={alertData.start_date}
                onChange={(e) => setAlertData({...alertData, start_date: e.target.value})}
                required
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <Label htmlFor="alert_end_date">End Date</Label>
              <Input
                id="alert_end_date"
                type="date"
                value={alertData.end_date}
                onChange={(e) => setAlertData({...alertData, end_date: e.target.value})}
                required
                min={alertData.start_date || new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <Label htmlFor="alert_email">Your Email</Label>
              <Input
                id="alert_email"
                type="email"
                placeholder="your@email.com"
                value={alertData.email}
                onChange={(e) => setAlertData({...alertData, email: e.target.value})}
                required
              />
            </div>
            <Button 
              type="submit" 
              className="w-full"
              style={{background: '#FF6B35'}}
            >
              Create Alert
            </Button>
          </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="py-12 mt-16" style={{backgroundColor: '#2D5016'}}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <h3 className="text-xl font-bold text-white mb-2" style={{fontFamily: 'Lobster Two, cursive'}}>CampSight</h3>
            <p className="text-green-100 text-sm">Camp at first Sight.</p>
          </div>
          
          <div className="border-t border-green-700 pt-6 text-center">
            <p className="text-green-100 text-sm">© 2025 CampSight. Camp at first Sight.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
